import { useEffect, useState } from 'react';
import { LogOut, Flame, Coins, Sparkles } from 'lucide-react';
import { auth, profile as profileApi, tokens as tokensApi, fields } from '../lib/api.js';
import { useSession } from '../lib/session.jsx';
import { useCrystal } from '../lib/crystalState.jsx';
import { Glass, Button, Field, Tag, Empty, Heading, Stat } from '../components/ui.jsx';
import Crystal from '../components/Crystal.jsx';
import DeviceManager from '../components/DeviceManager.jsx';
import KeeperTools from '../components/KeeperTools.jsx';

export default function Profile() {
  const { profile, tokens, isAdmin, refresh } = useSession();
  const { info, hue, seed, state, loaded } = useCrystal();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [myFields, setMyFields] = useState([]);

  useEffect(() => { setName(profile?.full_name ?? ''); }, [profile?.full_name]);
  useEffect(() => {
    tokensApi.history().then(setLedger).catch(console.error);
    fields.mine().then(setMyFields).catch(console.error);
  }, []);

  // Anchor support: /profile#devices scrolls straight to device management.
  useEffect(() => {
    if (location.hash === '#devices') {
      document.getElementById('devices')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  const save = async () => {
    setSaving(true);
    try { await profileApi.rename(name); await refresh(); }
    finally { setSaving(false); }
  };

  const tone = (s) => (s === 'active' ? 'active' : s === 'building' ? 'building' : 'expired');

  return (
    <div className="space-y-4 md:space-y-5">
      <header className="rise rise-1 pt-2 pb-2 md:pb-4">
        <p className="eyebrow">Your account</p>
        <h1 className="display text-[2.6rem] md:text-[3.4rem] mt-2">
          This is <span className="display-i lit">you</span>.
        </h1>
      </header>

      {/* account + crystal */}
      <Glass className="rise rise-2 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: `radial-gradient(50% 60% at 18% 40%, hsl(${hue} 90% 65% / 0.18), transparent 70%)` }} />
        <div className="relative grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="flex items-center gap-4 md:flex-col md:items-center md:text-center">
            <div className="-my-4">
              {loaded ? <Crystal xp={state.xp} hue={hue} seed={seed} size={150} /> : <div className="h-[157px] w-[140px]" />}
            </div>
            <div>
              <p className="display text-2xl">{loaded ? info.name : '…'}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>
                Level {loaded ? info.level : '—'} · {loaded ? state.xp : 0} xp
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm" style={{ color: 'var(--text-3)' }}>{profile?.email}</p>
              {isAdmin && <Tag tone="warn">Keeper</Tag>}
            </div>
            <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
            <div className="flex gap-2 flex-wrap">
              <Button onClick={save} disabled={saving || name === profile?.full_name}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="quiet" onClick={() => auth.signOut()}>
                <LogOut size={14} /> Sign out
              </Button>
            </div>
          </div>
        </div>

        {loaded && (
          <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Stat label="Attunements" value={state.attunements} />
            <Stat label="Streak" value={`${state.streak}d`} accent={state.streak > 0 ? 'violet' : undefined} />
            <Stat label="Months held" value={state.moons_held} />
            <Stat label="Tokens given" value={state.tokens_given} accent="gold" />
            <Stat label="Age" value={`${state.age_days}d`} />
          </div>
        )}
      </Glass>

      <div className="rise rise-3"><KeeperTools />

      <DeviceManager /></div>

      <Glass className="rise rise-4">
        <Heading eyebrow="Consecrated ground" title="Field history" />
        {myFields.length === 0 ? <Empty>No fields created yet.</Empty> : (
          <ul className="divide-y" style={{ borderColor: 'var(--edge)' }}>
            {myFields.slice(0, 20).map((f) => (
              <li key={f.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                  style={{ borderColor: 'var(--edge)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                        style={{ background: 'color-mix(in oklab, var(--sage) 14%, transparent)', color: 'var(--sage)' }}>
                    <Flame size={15} />
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold capitalize leading-tight">{f.intensity}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                      {f.radius_m}m · {f.is_public ? 'Public' : 'Private'}
                    </p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <Tag tone={tone(f.status)} dot>{f.status}</Tag>
                  <p className="text-[0.7rem] mt-1.5" style={{ color: 'var(--text-3)' }}>
                    {new Date(f.created_at).toLocaleDateString()}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Glass>

      <Glass className="rise rise-5">
        <Heading eyebrow="Offerings" title="Token history"
          action={<span className="text-sm font-semibold nums" style={{ color: 'var(--gold)' }}>{tokens} available</span>} />
        {ledger.length === 0 ? <Empty>No transactions yet.</Empty> : (
          <ul className="divide-y" style={{ borderColor: 'var(--edge)' }}>
            {ledger.slice(0, 20).map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                  style={{ borderColor: 'var(--edge)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-9 w-9 rounded-full grid place-items-center shrink-0"
                        style={{ background: 'var(--glass-2)', color: t.delta > 0 ? 'var(--gold)' : 'var(--text-3)' }}>
                    {t.delta > 0 ? <Sparkles size={15} /> : <Coins size={15} />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium capitalize leading-tight">{t.reason.replace(/_/g, ' ')}</p>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                      {new Date(t.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className="display text-xl nums" style={{ color: t.delta > 0 ? 'var(--sage)' : 'var(--text)' }}>
                  {t.delta > 0 ? '+' : ''}{t.delta}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Glass>
    </div>
  );
}
